import { IModuleConfig, ISlotConfig } from 'redux-state/layout/types.modules.js';
import { IPageConfig } from 'redux-state/layout/types.page.js';
import { removeQueryParam } from 'redux-state/routing/utils.params.js';
import { PageLocationSummary } from 'types/markerTypes/location.page.js';
import { makeRequest } from 'utilities/fetch/api-gateway.js';
import { messageLog } from 'utilities/helpers/logger/message-logger.js';

type MCPMessageModule = {
  module: IModuleConfig;
  slotPath: string;
};

type MCPData = {
  messageModules: MCPMessageModule[];
  url: string;
};

function valOrDefault(val: string | undefined) {
  return val ?? 'none';
}

const removeLeadingSlash = (url: string) => {
  if (url[0] === '/') {
    return url.slice(1);
  }
  return url;
};

const getBit = (websiteSection: string | undefined, url: string | undefined) => {
  if (websiteSection === 'imagery' && url) {
    const urlWithNoParams = removeQueryParam(url);
    const urlWithNoSlash = removeLeadingSlash(urlWithNoParams);
    const updateUrl = urlWithNoSlash.replaceAll('/', '+');
    return updateUrl;
  }
  return undefined;
};

export const getMcpUrl = (
  location:
    | Pick<PageLocationSummary, 'parentSection' | 'websiteSection' | 'websiteRegion' | 'websiteLocation'>
    | undefined,
  url: string | undefined,
) => {
  //https://metservice.atlassian.net/wiki/spaces/PWM/pages/101655532/Message+Control+Panel+API+for+Nice+Locations
  if (location) {
    const { parentSection, websiteSection, websiteRegion, websiteLocation } = location;
    const bit = getBit(websiteSection, url);

    // Because for the coastal and recreational marine pages they are standalone locations
    return `/mcp-messages/${valOrDefault(parentSection)}/${valOrDefault(websiteSection)}/${valOrDefault(
      websiteRegion,
    )}/${valOrDefault(websiteLocation)}/${valOrDefault(bit)}`;
  }
};

/**
 * Requests mcp data and merges into pageLayout
 * http://wiki.met.co.nz/display/PWM/Message+Control+Panel+API+for+Nice+Locations
 */
const MCP_INIT = true;
export const mergeMcpData = async (pageConfig: IPageConfig<'Raw'>) => {
  const mcpUrl = getMcpUrl(pageConfig.location, pageConfig.url);
  if (MCP_INIT && mcpUrl) {
    // messageLog.info(`Trying to get MCP ${mcpUrl}`);
    try {
      const options: RequestInit = { credentials: 'include' };
      const mcpData = await makeRequest<MCPData>({ path: mcpUrl, options, apiPrefix: true });
      // messageLog.info(`Got MCPData`, ['mcpData', mcpData]);
      if (mcpData) {
        for (const { module, slotPath } of mcpData.messageModules) {
          const moduleArr = mcpBuildModules(slotPath, pageConfig);
          // mutate here by pushing into the object
          moduleArr.push(module);
        }
      }
    } catch (e) {
      messageLog.info(`Unable to get mcp ${mcpUrl}`);
      return pageConfig;
    }
  }
  return pageConfig;
};

type MCPCoreModule = {
  [name: string]: MCPCoreModule;
};

const mcpBuildModules = (slotPath: string, _pageConfig: IPageConfig<'Raw'>): IModuleConfig[] => {
  // we cast here so we can easily move through the object and create paths as needed
  const pageConfig = _pageConfig as unknown as MCPCoreModule;
  const slotPathArr = slotPath.split(',');
  let newPageConfig = pageConfig;
  slotPathArr.forEach((_pathItem) => {
    // remove any dodgy white space
    const pathItem = _pathItem.trim();
    if (!newPageConfig[pathItem]) {
      newPageConfig[pathItem] = {};
    }
    newPageConfig = newPageConfig[pathItem];
  });
  if (newPageConfig['modules'] && Array.isArray(newPageConfig['modules'])) {
    return newPageConfig['modules'];
  } else {
    (newPageConfig as unknown as ISlotConfig)['modules'] = [] as IModuleConfig[];
  }
  return newPageConfig['modules'] as unknown as IModuleConfig[];
};
